import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Menu, ChevronRight, Calendar, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import ChatWidget from '@/components/ChatWidget';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/54bsgtxo_logo.png";

const PrivacyPolicyPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [startBookingOnOpen, setStartBookingOnOpen] = useState(false);

  const openChatForBooking = () => {
    setStartBookingOnOpen(true);
    setChatOpen(true);
  };

  const handleChatOpenChange = (open) => {
    setChatOpen(open);
    if (!open) {
      setStartBookingOnOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Privacy Policy | iSalon UK | Hair Salon Southport</title>
        <meta name="description" content="Privacy Policy for iSalon UK. Learn how we collect, use, and protect your personal information when you use our website and services." />
        <link rel="canonical" href="https://www.isalon.uk/privacy-policy" />
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/">
              <img src={LOGO_URL} alt="iSalon UK" className="h-10 md:h-12 w-auto" />
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">Home</Link>
              <Link to="/about" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">About</Link>
              <Link to="/prices" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">Price List</Link>
              <Link to="/#reviews" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">Reviews</Link>
              <Link to="/opportunities" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">Join Us</Link>
              <Button onClick={openChatForBooking} className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full px-6">
                Book Now
              </Button>
            </div>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <button className="p-2 rounded-lg text-white">
                  <Menu size={40} strokeWidth={2.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm bg-black border-l border-white/10 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-white/10">
                    <img src={LOGO_URL} alt="iSalon UK" className="h-10 w-auto mb-4" />
                    <SheetClose asChild>
                      <Button onClick={openChatForBooking} className="w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full py-5 text-lg">
                        <Calendar className="mr-2" size={20} />
                        Book Now
                      </Button>
                    </SheetClose>
                  </div>
                  <div className="flex-1 p-6 overflow-y-auto">
                    <nav className="flex flex-col gap-4">
                      <SheetClose asChild>
                        <Link to="/" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Home
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/about" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          About
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/prices" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Price List
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/opportunities" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Join Us
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/#contact" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Contact Us
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                    </nav>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-isalonuk-accent transition-colors mb-8">
            <ArrowLeft size={18} />
            <span className="font-sans text-sm">Back to Home</span>
          </Link>

          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-8">Privacy Policy</h1>
          <p className="text-white/60 font-sans mb-8">Last updated: March 2026</p>

          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">1. Introduction</h2>
              <p className="text-white/70 font-sans leading-relaxed">
                iSalon UK ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website www.isalon.uk or use our services.
              </p>
              <p className="text-white/70 font-sans leading-relaxed mt-4">
                iSalon UK is a trading name of Scissors Limited, a company registered in England and Wales with company registration number 11875206. Our registered address is 89B London St, Southport, PR9 0TX.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">2. Information We Collect</h2>
              <p className="text-white/70 font-sans leading-relaxed mb-4">We may collect the following types of information:</p>
              <ul className="list-disc list-inside text-white/70 font-sans space-y-2 ml-4">
                <li><strong className="text-white">Personal Information:</strong> Name, email address, phone number, and any other information you provide when booking an appointment or contacting us.</li>
                <li><strong className="text-white">Appointment Information:</strong> Your preferred stylist, date, time, and service preferences.</li>
                <li><strong className="text-white">Communication Preferences:</strong> How you prefer to be contacted (email, SMS, or WhatsApp).</li>
                <li><strong className="text-white">Usage Data:</strong> Information about how you use our website, including pages visited and features used.</li>
                <li><strong className="text-white">Cookies:</strong> Small data files stored on your device to improve your browsing experience.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">3. How We Use Your Information</h2>
              <p className="text-white/70 font-sans leading-relaxed mb-4">We use the information we collect to:</p>
              <ul className="list-disc list-inside text-white/70 font-sans space-y-2 ml-4">
                <li>Process and manage your appointment bookings</li>
                <li>Send appointment confirmations and reminders</li>
                <li>Respond to your enquiries and provide customer support</li>
                <li>Improve our website and services</li>
                <li>Send promotional communications (only with your consent)</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">4. Legal Basis for Processing</h2>
              <p className="text-white/70 font-sans leading-relaxed">
                Under UK GDPR, we process your personal data based on the following legal grounds:
              </p>
              <ul className="list-disc list-inside text-white/70 font-sans space-y-2 ml-4 mt-4">
                <li><strong className="text-white">Contract:</strong> To fulfil our appointment booking services</li>
                <li><strong className="text-white">Legitimate Interests:</strong> To improve our services and communicate with you</li>
                <li><strong className="text-white">Consent:</strong> For marketing communications and non-essential cookies</li>
                <li><strong className="text-white">Legal Obligation:</strong> To comply with applicable laws</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">5. Data Sharing</h2>
              <p className="text-white/70 font-sans leading-relaxed">
                We do not sell your personal information. We may share your data with:
              </p>
              <ul className="list-disc list-inside text-white/70 font-sans space-y-2 ml-4 mt-4">
                <li>Our staff members who need the information to provide services</li>
                <li>Service providers who assist with our operations (e.g., email services)</li>
                <li>Legal authorities when required by law</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">6. Cookies</h2>
              <p className="text-white/70 font-sans leading-relaxed">
                Our website uses cookies to enhance your browsing experience. You can manage your cookie preferences through your browser settings or by using our cookie consent banner.
              </p>
              <p className="text-white/70 font-sans leading-relaxed mt-4">
                We use essential cookies for website functionality and analytics cookies to understand how visitors use our site.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">7. Data Retention</h2>
              <p className="text-white/70 font-sans leading-relaxed">
                We retain your personal information for as long as necessary to provide our services and comply with legal obligations. Appointment records are typically retained for 7 years for business and legal purposes.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">8. Your Rights</h2>
              <p className="text-white/70 font-sans leading-relaxed mb-4">Under UK GDPR, you have the right to:</p>
              <ul className="list-disc list-inside text-white/70 font-sans space-y-2 ml-4">
                <li>Access your personal data</li>
                <li>Rectify inaccurate data</li>
                <li>Request erasure of your data</li>
                <li>Restrict processing of your data</li>
                <li>Data portability</li>
                <li>Object to processing</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="text-white/70 font-sans leading-relaxed mt-4">
                To exercise any of these rights, please contact us at bookings@isalon.uk.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">9. Data Security</h2>
              <p className="text-white/70 font-sans leading-relaxed">
                We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">10. Third-Party Links</h2>
              <p className="text-white/70 font-sans leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">11. Changes to This Policy</h2>
              <p className="text-white/70 font-sans leading-relaxed">
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white font-semibold mb-4">12. Contact Us</h2>
              <p className="text-white/70 font-sans leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="mt-4 text-white/70 font-sans space-y-2">
                <p><strong className="text-white">Email:</strong> bookings@isalon.uk</p>
                <p><strong className="text-white">Phone:</strong> 01704 500872</p>
                <p><strong className="text-white">Address:</strong> 89B London St, Southport, PR9 0TX</p>
              </div>
              <p className="text-white/70 font-sans leading-relaxed mt-4">
                You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe your data has been mishandled: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-isalonuk-accent hover:underline">ico.org.uk</a>
              </p>
            </section>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-900 py-12 px-6 md:px-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center gap-4 text-center">
            <img src={LOGO_URL} alt="iSalon UK" className="h-10 w-auto mb-4" />
            <p className="text-white/40 font-sans text-sm">© {new Date().getFullYear()} iSalon UK. All rights reserved.</p>
            <p className="text-white/40 font-sans text-xs leading-relaxed max-w-lg">
              iSalon UK is a trading name of Scissors Limited, a company registered in England and Wales with company registration number 11875206.
            </p>
            <p className="text-isalonuk-accent font-sans text-sm font-medium">
              Website designed and built by <a href="https://www.barton.uk" target="_blank" rel="noopener noreferrer" className="hover:underline">www.barton.uk</a>
            </p>
          </div>
        </div>
      </footer>

      {/* Chat Widget */}
      <ChatWidget 
        isOpen={chatOpen} 
        onOpenChange={handleChatOpenChange} 
        startBookingOnOpen={startBookingOnOpen}
        autoOpen={false}
      />
    </div>
  );
};

export default PrivacyPolicyPage;
